import React, { memo, useMemo, useState, useEffect } from "react";
import { MessageSegment } from "../utils/messages";
import { cn } from "@/lib/utils";
import { ChevronDown, ChevronRight, Brain } from "lucide-react";

interface ParsedText {
  type: "text" | "bold" | "italic";
  content: string;
}

const parseMarkdown = (text: string): ParsedText[] => {
  const parts: ParsedText[] = [];
  let currentText = "";
  let i = 0;

  while (i < text.length) {
    if (text.startsWith("**", i)) {
      if (currentText) {
        parts.push({ type: "text", content: currentText });
        currentText = "";
      }
      i += 2;
      let boldText = "";
      while (i < text.length && !text.startsWith("**", i)) {
        boldText += text[i];
        i++;
      }
      i += 2;
      parts.push({ type: "bold", content: boldText });
    } else if (text.startsWith("*", i)) {
      if (currentText) {
        parts.push({ type: "text", content: currentText });
        currentText = "";
      }
      i++;
      let italicText = "";
      while (i < text.length && text[i] !== "*") {
        italicText += text[i];
        i++;
      }
      i++;
      parts.push({ type: "italic", content: italicText });
    } else {
      currentText += text[i];
      i++;
    }
  }

  if (currentText) {
    parts.push({ type: "text", content: currentText });
  }

  return parts;
};

interface ChatMessageProps {
  block: MessageSegment;
  allSegments: MessageSegment[];
}

export const ReasoningChatMessage = memo(
  ({ block, allSegments }: ChatMessageProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const {
      parsedReasoningLines,
      searchSummary,
      planOfAction,
      affectedFiles,
      thinkingTimeSeconds,
    } = useMemo(() => {
      try {
        if (block.reasoning) {
          const reasoning = block.reasoning.content || "";
          const parsedReasoningLines = reasoning.split("\n").map((line) => {
            return parseMarkdown(line);
          });

          return {
            parsedReasoningLines,
            searchSummary: "",
            planOfAction: "",
            affectedFiles: [],
            thinkingTimeSeconds: block.reasoning.thinkingTimeSeconds ?? null,
          };
        }

        // OLD: Fall back to XML-based parsedJson (custom reasoning)
        const parsedJson = block.parsedJson;
        const reasoning = parsedJson?.reasoning || "";
        const search = parsedJson?.summary_of_search_results || "";
        const plan = parsedJson?.plan_of_action || "";
        const files: string[] = parsedJson?.affected_files || [];

        // Find thinking time from reasoning-timing segment (only for old format)
        let thinkingTime = null;
        const timingSegment = allSegments.find(
          (seg) => seg.type === "reasoning-timing",
        );
        if (timingSegment && timingSegment.parsedJson) {
          thinkingTime = timingSegment.parsedJson.thinking_time_seconds || null;
        }

        // Perform the rendering inside the
        const parsedReasoningLines = reasoning.split("\n").map((line) => {
          return parseMarkdown(line);
        });

        return {
          parsedReasoningLines,
          searchSummary: search,
          planOfAction: plan,
          affectedFiles: files,
          thinkingTimeSeconds: thinkingTime,
        };
      } catch (error) {
        console.error("Failed to parse reasoning JSON:", error);
        return {
          parsedReasoningLines: [],
          searchSummary: "",
          planOfAction: "",
          affectedFiles: [],
          thinkingTimeSeconds: null,
        };
      }
    }, [block.reasoning, block.parsedJson, allSegments]);

    const hasContent = useMemo(() => {
      return (
        (parsedReasoningLines &&
          parsedReasoningLines.length > 0 &&
          parsedReasoningLines.some((line) => line.length > 0)) ||
        (searchSummary &&
          typeof searchSummary === "string" &&
          searchSummary.trim()) ||
        (planOfAction &&
          typeof planOfAction === "string" &&
          planOfAction.trim()) ||
        (Array.isArray(affectedFiles) &&
          affectedFiles.filter(
            (file) => typeof file === "string" && file.trim(),
          ).length > 0)
      );
    }, [parsedReasoningLines, searchSummary, planOfAction, affectedFiles]);

    // Auto-expand/collapse based on reasoning completion from backend
    useEffect(() => {
      if (!thinkingTimeSeconds) {
        // If no thinking time yet, reasoning is still processing - keep open
        setIsExpanded(true);
      } else {
        // Thinking is complete (backend provided thinking_time_seconds), collapse the accordion
        setIsExpanded(false);
      }
    }, [thinkingTimeSeconds]);

    if (!hasContent) {
      return null;
    }

    return (
      <div className="my-0.5">
        <div
          className="flex items-center gap-2 py-2 cursor-pointer text-secondary-n-400 text-xs font-medium hover:no-underline group/reasoning"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2">
            <div className="relative w-4 h-4">
              {/* Chevron */}
              <ChevronRight
                className={cn(
                  "w-4 h-4 text-secondary-n-400 transition-all absolute inset-0",
                  isExpanded
                    ? "rotate-90 opacity-100"
                    : "opacity-0 group-hover/reasoning:opacity-100",
                )}
              />
              {/* Brain icon */}
              <div
                className={cn(
                  "absolute inset-0 transition-opacity",
                  isExpanded
                    ? "opacity-0"
                    : "opacity-100 group-hover/reasoning:opacity-0",
                )}
              >
                <Brain className="w-4 h-4 text-secondary-n-400" />
              </div>
            </div>
            <span
              className={cn(
                "font-semibold text-xs",
                !thinkingTimeSeconds &&
                  "text-transparent bg-clip-text bg-linear-to-r from-gray-900 via-white to-gray-900 animate-shimmer bg-size-[200%_100%]",
              )}
            >
              {thinkingTimeSeconds
                ? `Thought for ${thinkingTimeSeconds} seconds`
                : "Thinking"}
            </span>
          </div>
        </div>

        {isExpanded && (
          <div className="pb-2 transition-all">
            <div className="relative ml-6">
              <div className="absolute left-[-18px] top-0 bottom-0 w-px bg-secondary-n-700"></div>
              <div className="flex flex-col">
                {parsedReasoningLines &&
                  parsedReasoningLines.length > 0 &&
                  parsedReasoningLines.some((line) => line.length > 0) && (
                    <div className="pt-3">
                      <div className="text-xs font-medium text-secondary-n-300 mb-2">
                        Reasoning
                      </div>
                      <div className="text-xs py-0.5 text-secondary-n-400 text-left whitespace-pre-line">
                        {parsedReasoningLines
                          .map((line, lineIndex) => {
                            return line.map((part, partIndex) => {
                              switch (part.type) {
                                case "bold":
                                  return (
                                    <strong key={`${lineIndex}-${partIndex}`}>
                                      {part.content}
                                    </strong>
                                  );
                                case "italic":
                                  return (
                                    <em key={`${lineIndex}-${partIndex}`}>
                                      {part.content}
                                    </em>
                                  );
                                default:
                                  return (
                                    <span key={`${lineIndex}-${partIndex}`}>
                                      {part.content}
                                    </span>
                                  );
                              }
                            });
                          })
                          .map((line, i) => {
                            return (
                              <React.Fragment key={i}>
                                <span>{line}</span>
                                <span>
                                  {i < parsedReasoningLines.length - 1 && (
                                    <br />
                                  )}
                                </span>
                              </React.Fragment>
                            );
                          })}
                      </div>
                    </div>
                  )}

                {planOfAction &&
                  typeof planOfAction === "string" &&
                  planOfAction.trim() && (
                    <div className="pt-3">
                      <div className="text-xs font-medium text-secondary-n-300 mb-2">
                        Plan of action
                      </div>
                      <div className="text-xs py-0.5 text-secondary-n-400 text-left whitespace-pre-line">
                        {planOfAction.split("\n").map((line, i) => (
                          <React.Fragment key={i}>
                            <span>{line}</span>
                            <span>
                              {i < planOfAction.split("\n").length - 1 && (
                                <br />
                              )}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}

                {searchSummary &&
                  typeof searchSummary === "string" &&
                  searchSummary.trim() && (
                    <div className="pt-3">
                      <div className="text-xs font-medium text-secondary-n-300 mb-2">
                        Search Summary
                      </div>
                      <div className="text-xs font-mono py-0.5 px-2 text-secondary-n-400 break-all whitespace-pre-line bg-secondary-n-900/30 rounded">
                        {searchSummary.split("\n").map((line, i) => (
                          <React.Fragment key={i}>
                            <span>{line}</span>
                            <span>
                              {i < searchSummary.split("\n").length - 1 && (
                                <br />
                              )}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )}

                {Array.isArray(affectedFiles) &&
                  affectedFiles.filter(
                    (file) => typeof file === "string" && file.trim(),
                  ).length > 0 && (
                    <div className="pt-3">
                      <div className="text-xs font-medium text-secondary-n-300 mb-2">
                        Affected Files
                      </div>
                      <div className="text-xs py-0.5 text-secondary-n-400 break-all">
                        {affectedFiles
                          .filter(
                            (file) => typeof file === "string" && file.trim(),
                          )
                          .join(", ")}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);
